import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, doc, getDoc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { FiArrowLeft, FiPlay, FiChevronDown, FiYoutube, FiFilm } from 'react-icons/fi';
import Loader from '../components/Loader';
import TrailerModal from '../components/TrailerModal';

const SeriesPage = () => {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const [series,   setSeries]   = useState(null);
  const [seasons,  setSeasons]  = useState([]);
  const [expanded, setExpanded] = useState(0);
  const [episodes, setEpisodes] = useState({});   // { seasonId: [episode] }
  const [playing,  setPlaying]  = useState(null); // { youtubeId }
  const [loading,  setLoading]  = useState(true);

  // Load series doc
  useEffect(() => {
    getDoc(doc(db, 'series', id))
      .then(snap => snap.exists() && setSeries({ id: snap.id, ...snap.data() }))
      .finally(() => setLoading(false));
  }, [id]);

  // Load seasons
  useEffect(() => {
    const q = query(collection(db, 'series', id, 'seasons'), orderBy('order', 'asc'));
    return onSnapshot(q, snap => setSeasons(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [id]);

  // Load episodes for a season when expanded
  useEffect(() => {
    if (seasons.length === 0) return;
    const season = seasons[expanded];
    if (!season || episodes[season.id]) return;
    const q = query(collection(db, 'series', id, 'seasons', season.id, 'episodes'), orderBy('order', 'asc'));
    const unsub = onSnapshot(q, snap =>
      setEpisodes(prev => ({ ...prev, [season.id]: snap.docs.map(d => ({ id: d.id, ...d.data() })) }))
    );
    return unsub;
  }, [expanded, seasons, id, episodes]);

  if (loading) return <Loader full />;
  if (!series) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--dark)' }}>
      <div className="text-center">
        <FiFilm size={48} className="mx-auto mb-4" style={{ color: 'var(--border)' }} />
        <p className="font-display text-2xl" style={{ color: 'var(--muted)' }}>Series not found</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-sm font-body" style={{ color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer' }}>← Go back</button>
      </div>
    </div>
  );

  const currentSeason = seasons[expanded];

  return (
    <div style={{ background: 'var(--dark)', minHeight: '100vh' }}>
      {/* Banner */}
      <div className="relative w-full overflow-hidden" style={{ height: '55vh', minHeight: 360 }}>
        {series.backdropUrl && (
          <img src={series.backdropUrl} alt={series.title}
            className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'brightness(0.32)' }} />
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right,rgba(10,10,15,0.98) 0%,rgba(10,10,15,0.5) 55%,transparent 85%)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top,var(--dark) 0%,transparent 60%)' }} />

        <motion.button initial={{ x: -24, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
          whileHover={{ x: -4 }} onClick={() => navigate(-1)}
          className="absolute top-24 left-6 md:left-12 flex items-center gap-2 px-4 py-2 rounded-full glass z-10"
          style={{ color: 'var(--text)', fontSize: 13 }}>
          <FiArrowLeft size={15} /> Back
        </motion.button>

        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 pb-8 flex gap-6 items-end">
          {series.posterUrl && (
            <div className="hidden md:block flex-shrink-0 rounded-2xl overflow-hidden"
              style={{ width: 140, height: 210, border: '2px solid rgba(229,9,20,0.4)' }}>
              <img src={series.posterUrl} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2.5 py-0.5 rounded-full text-xs font-accent"
                style={{ background: 'rgba(229,9,20,0.2)', color: 'var(--red)', border: '1px solid rgba(229,9,20,0.3)' }}>
                SERIES
              </span>
              {series.genre && <span className="text-xs font-body" style={{ color: 'var(--muted)' }}>{series.genre}</span>}
            </div>
            <h1 className="font-display text-4xl md:text-6xl leading-none mb-2" style={{ color: 'var(--text)' }}>{series.title}</h1>
            <p className="text-sm font-body max-w-xl" style={{ color: 'rgba(232,232,240,0.7)' }}>{series.description}</p>
            <div className="flex items-center gap-3 mt-3 text-xs font-body" style={{ color: 'var(--muted)' }}>
              <span>{seasons.length} Season{seasons.length !== 1 ? 's' : ''}</span>
              {series.releaseYear && <><span>·</span><span>{series.releaseYear}</span></>}
            </div>
          </div>
        </div>
      </div>

      {/* Season / Episode browser */}
      <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-10">
        {seasons.length === 0 ? (
          <div className="text-center py-16">
            <p className="font-body" style={{ color: 'var(--muted)' }}>No seasons added yet.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {seasons.map((season, si) => {
              const isOpen = expanded === si;
              const eps    = episodes[season.id] || [];
              return (
                <motion.div key={season.id} layout
                  className="rounded-2xl overflow-hidden"
                  style={{ border: isOpen ? '1px solid rgba(229,9,20,0.3)' : '1px solid rgba(255,255,255,0.06)', background: 'rgba(17,17,24,0.8)' }}>
                  {/* Season header */}
                  <button onClick={() => setExpanded(isOpen ? -1 : si)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left"
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                    <div>
                      <p className="font-display text-xl tracking-wide" style={{ color: 'var(--text)' }}>
                        {season.title || `Season ${si + 1}`}
                      </p>
                      <p className="text-xs font-body mt-0.5" style={{ color: 'var(--muted)' }}>
                        {eps.length > 0 ? `${eps.length} episodes` : 'Loading…'}
                      </p>
                    </div>
                    <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.22 }}>
                      <FiChevronDown size={20} style={{ color: 'var(--muted)' }} />
                    </motion.div>
                  </button>

                  {/* Episode list */}
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
                        className="overflow-hidden">
                        <div className="border-t px-6 pb-4" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                          {eps.length === 0 ? (
                            <p className="py-6 text-center text-sm font-body" style={{ color: 'var(--muted)' }}>No episodes yet.</p>
                          ) : (
                            <div className="flex flex-col gap-2 mt-4">
                              {eps.map((ep, ei) => (
                                <motion.div key={ep.id}
                                  initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: ei * 0.03 }}
                                  className="flex items-center gap-4 p-3 rounded-xl cursor-pointer group transition-colors"
                                  style={{ background: 'rgba(255,255,255,0.03)' }}
                                  onClick={() => ep.youtubeId && setPlaying(ep.youtubeId)}
                                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(229,9,20,0.08)'}
                                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                                >
                                  {/* Thumb */}
                                  <div className="flex-shrink-0 relative rounded-lg overflow-hidden"
                                    style={{ width: 100, height: 58, background: '#1a1a2e' }}>
                                    {ep.thumbnailUrl
                                      ? <img src={ep.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                                      : ep.youtubeId
                                      ? <img src={`https://img.youtube.com/vi/${ep.youtubeId}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                                      : <div className="w-full h-full flex items-center justify-center"><FiFilm size={20} style={{ color: 'var(--border)' }} /></div>
                                    }
                                    {ep.youtubeId && (
                                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                        style={{ background: 'rgba(0,0,0,0.5)' }}>
                                        <FiPlay size={22} fill="white" color="white" />
                                      </div>
                                    )}
                                  </div>

                                  {/* Info */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                      <span className="text-xs font-accent" style={{ color: 'var(--muted)' }}>E{ei + 1}</span>
                                      <p className="text-sm font-body font-medium truncate" style={{ color: 'var(--text)' }}>{ep.title}</p>
                                    </div>
                                    {ep.description && (
                                      <p className="text-xs line-clamp-2" style={{ color: 'var(--muted)' }}>{ep.description}</p>
                                    )}
                                    <div className="flex items-center gap-3 mt-1">
                                      {ep.duration && <span className="text-xs font-accent" style={{ color: 'var(--muted)' }}>{ep.duration}</span>}
                                      {ep.youtubeId && (
                                        <span className="flex items-center gap-1 text-xs" style={{ color: '#ff4444' }}>
                                          <FiYoutube size={11} /> YouTube
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {ep.youtubeId && (
                                    <motion.div whileHover={{ scale: 1.1 }}
                                      className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                                      style={{ background: 'var(--red)' }}>
                                      <FiPlay size={14} fill="white" color="white" />
                                    </motion.div>
                                  )}
                                </motion.div>
                              ))}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <TrailerModal videoKey={playing} onClose={() => setPlaying(null)} />
    </div>
  );
};

export default SeriesPage;
